import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { ExerciseKind } from './exercise';

/**
 * Screen copy for the three exercises, shared so web
 * and mobile never drift. Message descriptors rather than strings: screens render them in
 * the active language with `useLingui.t(copy.title)`. The passage and the sum are
 * placeholder content until OnEarly serves localised exercises (docs/decisions/0004-data.md).
 */
export interface ExerciseCopy {
  /** Name of the exercise — Home rows and the screen header. */
  title: MessageDescriptor;
  /** One line under the title on Home. */
  subtitle: MessageDescriptor;
  /** Coach screen before the exercise. */
  intro: {
    /** The speech bubble: one short paragraph, so the coach and the buttons fit above the fold. */
    body: MessageDescriptor;
    /** Reassurance next to the info icon under the illustration. */
    hint: MessageDescriptor;
  };
  /**
   * "What would you like to…?" chooser for reading and writing (math has its own, over the
   * Math library). The
   * first option is always "Pick for me" (the workbook); the others are the person's own
   * material, with `body` as the instruction the exercise shows for that choice.
   */
  choose?: { title: MessageDescriptor; options: ChooseOption[] };
  /**
   * Order of the screens around the exercise itself. Reading and writing ask what to
   * read/write first, then show the coach; writing ends by reading the page out loud.
   */
  flow: readonly ExerciseStep[];
  /** After the exercise: the coach asks to read what was written out loud. */
  readback?: { body: MessageDescriptor };
  /** Per-exercise completion bubble: one paragraph, the praise and what was completed. */
  complete: { body: MessageDescriptor };
  /** Instruction at the top of the exercise. */
  prompt: MessageDescriptor;
  heading?: MessageDescriptor;
  body?: MessageDescriptor;
  /** Reassurance next to the info icon above the primary button. */
  hint: MessageDescriptor;
  /** Primary button label. */
  done: MessageDescriptor;
  /** Under the writing instructions: what to do with the photo (writing only). */
  photoHint?: MessageDescriptor;
  /** Shown under the options as soon as an answer is picked. */
  feedback?: { correct: MessageDescriptor; wrong: MessageDescriptor };
}

export interface ChooseOption {
  label: MessageDescriptor;
  /** The exercise's heading when this option is chosen instead of the workbook ("Make a to-do list"). */
  heading?: MessageDescriptor;
  /** What to do when this option is chosen instead of the workbook (writing categories). */
  body?: MessageDescriptor;
  /**
   * The option opens the Library to choose from instead of going on to the coach (reading);
   * shown only where the environment has the read-aloud library.
   */
  library?: boolean;
}

export type ExerciseStep = 'choose' | 'intro' | 'exercise' | 'readback';

/** The screen that follows `current` for this exercise (the first one for `undefined`); `complete` after the last. */
export function nextStep(
  kind: ExerciseKind,
  current: ExerciseStep | undefined,
): ExerciseStep | 'complete' {
  const flow = EXERCISE_COPY[kind].flow;
  const index = current ? flow.indexOf(current) : -1;
  return flow[index + 1] ?? 'complete';
}

export const EXERCISE_COPY: Record<ExerciseKind, ExerciseCopy> = {
  reading: {
    title: msg`Read aloud`,
    // Fallback for Home when no passage is provisioned; otherwise the next passage's title.
    subtitle: msg`Your own book or paper`,
    flow: ['choose', 'intro', 'exercise'],
    intro: {
      body: msg`Great choice! Get ready. Your brain training is about to start.`,
      hint: msg`The timer is to help track your time. You can tap the timer at any point to pause, resume or reset it.`,
    },
    choose: {
      title: msg`What would you like to read?`,
      options: [
        { label: msg`Pick for me` },
        { label: msg`My own book` },
        { label: msg`Something else` },
        { label: msg`Choose from Library`, library: true },
      ],
    },
    complete: { body: msg`Great job! You've completed today's reading exercise.` },
    prompt: msg`Read out loud, slowly and clearly`,
    hint: msg`Take your time. It's fine to re-read a sentence if you'd like to.`,
    done: msg`Done reading`,
  },
  writing: {
    title: msg`Write`,
    subtitle: msg`Describe your morning`,
    flow: ['choose', 'intro', 'exercise', 'readback'],
    intro: {
      body: msg`You'll need some paper and something to write with.`,
      hint: msg`Don't worry about your writing being neat or how well your spelling and grammar are.`,
    },
    choose: {
      title: msg`What would you like to write?`,
      // Each category is a heading the exercise shows and the instruction under it.
      options: [
        { label: msg`Pick for me` },
        {
          label: msg`Journal or diary entry`,
          heading: msg`Write about your day`,
          body: msg`In a few sentences, write about something that happened today and how you felt about it.`,
        },
        {
          label: msg`To-do list`,
          heading: msg`Make a to-do list`,
          body: msg`Write down a few things you would like to do today or over the next few days.`,
        },
        {
          label: msg`Grocery list`,
          heading: msg`Make a grocery list`,
          body: msg`Write down some food, drinks or household items you may need to buy.`,
        },
        {
          label: msg`Card, letter or note`,
          heading: msg`Write a note to someone`,
          body: msg`Think of someone you know. Write them a short card, letter or note about anything you'd like to share.`,
        },
        {
          label: msg`Recipe`,
          heading: msg`Write down a recipe`,
          body: msg`Think of a meal, snack or dish you know. Write the ingredients and the steps you would use to make it.`,
        },
        {
          label: msg`A reflection`,
          heading: msg`Take a moment to reflect`,
          body: msg`Think about something you've enjoyed, learned or noticed recently. Write a few sentences about it.`,
        },
      ],
    },
    complete: { body: msg`Nice work! You've completed today's writing exercise.` },
    readback: {
      body: msg`Nice work! You've completed today's writing exercise. Spend a few moments now to read out loud what you've written.`,
    },
    // Figma writing: the photo is an offer, never a requirement.
    photoHint: msg`Adding a photo is optional. You can include one if you'd like to view it again later.`,
    // Over whichever prompt the workbook serves, so it never contradicts the prompt under it; a
    // category of the person's own shows its own heading instead.
    prompt: msg`Today's writing prompt`,
    body: msg`In a few sentences, write what you did this morning - from the moment you woke up until now.`,
    hint: msg`There's no right or wrong answer. Write as much or as little as feels comfortable.`,
    done: msg`Done writing`,
  },
  math: {
    // "Quick math" in the v1.0 design; the workbook itself said "mental math".
    title: msg`Quick math`,
    subtitle: msg`Addition & subtraction`,
    // Where the environment has the Math library, "What would you like to practice?" first
    // (the screens skip it where it doesn't).
    flow: ['choose', 'intro', 'exercise'],
    intro: {
      body: msg`For this one, all you'll need... is your brain!`,
      hint: msg`Relax and take your time to work out the answer. There's no pressure to get it correct.`,
    },
    complete: { body: msg`You did it! You've completed today's math exercise.` },
    prompt: msg`Select an answer`,
    heading: msg`24 + 37 = ?`,
    hint: msg`One sum at a time. Pick the answer that looks right.`,
    // Always offered, like "Done reading" / "Done writing": nobody has to finish every sum
    // (QA note 10 — math was stuck as not-done once the timer ran out).
    done: msg`Done with math`,
    feedback: { correct: msg`🎉 Amazing`, wrong: msg`😅 So close` },
  },
};
